import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { ArrowLeft, Loader, CheckCircle } from 'lucide-react';

const ReconnectStep2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  
  const router = location.state?.router;
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [routerName, setRouterName] = useState('');
  const [hasExistingName, setHasExistingName] = useState(false);

  useEffect(() => {
    // Verificar si el router ya tiene nombre guardado
    if (router && router.name && router.name.trim() !== '') {
      setHasExistingName(true);
      setRouterName(router.name);
    }
  }, [router]);

  if (!router) {
    navigate('/routers');
    return null;
  }

  const handleConnect = async () => {
    try {
      setConnecting(true);
      
      const response = await api.post(`/api/routers/${router.id}/test-connection`);
      
      if (response.data.connected) {
        setConnected(true);
        alert('✅ Reconexión exitosa con el router');
      } else {
        alert('❌ No se pudo conectar al router. Verifica que el script se haya ejecutado correctamente.');
      }
      
    } catch (error) {
      console.error('Error conectando:', error);
      alert(error.response?.data?.detail || 'Error al conectar con el router');
    } finally {
      setConnecting(false);
    }
  };

  const handleFinish = () => {
    alert(`✅ Router "${routerName}" reconectado exitosamente`);
    navigate('/routers');
  };

  const handleBack = () => {
    navigate('/reconectar-step1', { 
      state: { 
        router: router
      } 
    });
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
            🔄 Reconectar Router
          </h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Paso 2 de 2: Verificar Conexión
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-green-600 rounded-full"></div>
            <div className="flex-1 h-2 bg-blue-600 rounded-full"></div>
          </div>
          <div className="flex justify-between mt-2 text-xs">
            <span className="text-green-600 font-semibold">✓ Script</span>
            <span className="text-blue-600 font-semibold">Conectar</span>
          </div>
        </div>

        {/* Datos de conexión */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 mb-6`}>
          <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            🔌 Datos de Conexión al Router
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm mb-6">
            <div>
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>IP del Router:</span>
              <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{router.ip_address}</p>
            </div>
            <div>
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Puerto API:</span>
              <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{router.port || 8728}</p>
            </div>
            <div>
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Usuario:</span>
              <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{router.username}</p>
            </div>
            <div>
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Nombre:</span>
              <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{router.name}</p>
            </div>
          </div>

          {!connected ? (
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {connecting ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Verificando conexión...
                </>
              ) : (
                'Verificar Conexión'
              )}
            </button>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <span className="text-green-800 font-semibold">✅ Conexión verificada correctamente</span>
            </div>
          )}
        </div>

        {/* Información del router */}
        {connected && (
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 mb-6`}>
            <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              ✅ Router Reconectado
            </h3>
            <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              El router <span className="font-bold">{routerName}</span> se ha reconectado exitosamente y está listo para usar.
            </p>
          </div>
        )}

        {/* Botones */}
        <div className="flex gap-4">
          <button
            onClick={handleBack}
            disabled={connecting}
            className={`px-6 py-3 rounded-lg font-semibold ${
              isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            } disabled:opacity-50`}
          >
            <ArrowLeft className="w-5 h-5 inline mr-2" />
            Atrás
          </button>
          
          {connected && (
            <button
              onClick={handleFinish}
              className="flex-1 px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white"
            >
              <CheckCircle className="w-5 h-5" />
              Finalizar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReconnectStep2;
